import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Modal from '~/components/Modal/Modal';
import { ThemeProp } from '~/theme/Themes';

const DirtyFormModalText = styled.p`
  color: ${({ theme }: ThemeProp) => theme.colors.neutrals.c400};
  font-size: ${({ theme }: ThemeProp) => theme.fontSize.s};
`;

interface DirtyFormContextProps {
  registerDirty: (key: string) => void;
  clearDirty: (key: string) => void;
  hasDirty: () => boolean;
  confirmIfDirty: (onConfirm: (wasDirty: boolean) => void) => void;
}

// Safe no-op defaults so any consumer rendered outside a DirtyFormProvider
// doesn't crash; navigation still proceeds since nothing is ever considered dirty.
const defaultValues: DirtyFormContextProps = {
  registerDirty: () => undefined,
  clearDirty: () => undefined,
  hasDirty: () => false,
  confirmIfDirty: (onConfirm) => onConfirm(false),
};

const DirtyFormContext = createContext<DirtyFormContextProps>(defaultValues);

interface DirtyFormProviderProps {
  children: ReactNode;
}

export const DirtyFormProvider: FC<DirtyFormProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const dirtyKeysRef = useRef<Set<string>>(new Set());
  const pendingActionRef = useRef<(() => void) | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const registerDirty = useCallback((key: string) => {
    dirtyKeysRef.current.add(key);
  }, []);

  const clearDirty = useCallback((key: string) => {
    dirtyKeysRef.current.delete(key);
  }, []);

  const hasDirty = useCallback(() => dirtyKeysRef.current.size > 0, []);

  const confirmIfDirty = useCallback(
    (onConfirm: (wasDirty: boolean) => void) => {
      if (hasDirty()) {
        pendingActionRef.current = () => onConfirm(true);
        setIsConfirmOpen(true);
        return;
      }
      onConfirm(false);
    },
    [hasDirty],
  );

  const handleLeave = () => {
    setIsConfirmOpen(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  };

  const handleStay = () => {
    setIsConfirmOpen(false);
    pendingActionRef.current = null;
  };

  return (
    <DirtyFormContext.Provider value={{ registerDirty, clearDirty, hasDirty, confirmIfDirty }}>
      {children}
      <Modal
        open={isConfirmOpen}
        title={t('common.unsavedChangesTitle')}
        submitButtonTitle={t('common.leavePage')}
        cancelButtonTitle={t('common.stayOnPage')}
        reverseActionsOrder
        onClose={handleStay}
        onCancel={handleStay}
        onSubmit={handleLeave}
      >
        <DirtyFormModalText>{t('common.unsavedChangesDescription')}</DirtyFormModalText>
      </Modal>
    </DirtyFormContext.Provider>
  );
};

export const useDirtyFormContext: () => DirtyFormContextProps = () => {
  return useContext(DirtyFormContext);
};

// Lets a create/edit form register its dirty state so a global org switch (or any other
// navigation-guarding action) can prompt an "unsaved changes" confirmation first.
export const useDirtyForm = (key: string, isDirty: boolean) => {
  const { registerDirty, clearDirty } = useDirtyFormContext();

  useEffect(() => {
    if (isDirty) {
      registerDirty(key);
    } else {
      clearDirty(key);
    }
  }, [isDirty, key, registerDirty, clearDirty]);

  useEffect(() => {
    return () => clearDirty(key);
  }, [key, clearDirty]);
};
